/**
 * Custom React hook for managing the 4 in a Row game state
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import { FourInARowEngine, AIPlayer } from '../gameLogic';
import {
  IGameConfig,
  IGameState,
  GameMode,
  PlayerType,
  AIDifficulty,
  GameStatus,
} from '../types/game-types';
import { playRandomMyTurnSound, playSound, SoundEffect } from '../utils/sound-utils';

interface IUseGameLogic {
  gameState: IGameState;
  makeMove: (column: number) => { success: boolean; error?: string };
  startNewGame: (config: IGameConfig) => void;
  resetGame: () => void;
  isAIThinking: boolean;
  aiDifficulty: AIDifficulty;
  setAIDifficulty: (difficulty: AIDifficulty) => void;
  lastAIMove: { column: number; row: number } | null;
}

export const useGameLogic = (): IUseGameLogic => {
  const engineRef = useRef<FourInARowEngine>(new FourInARowEngine());
  const [aiDifficulty, setAIDifficultyState] = useState<AIDifficulty>('medium');
  const aiPlayerRef = useRef<AIPlayer>(new AIPlayer('medium')); // Initialize with 'medium' to match state
  const [gameState, setGameState] = useState<IGameState>(engineRef.current.getGameState());
  const gameConfigRef = useRef<IGameConfig | null>(null);
  const [isAIThinking, setIsAIThinking] = useState(false);
  const aiMoveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const [aiTurnCount, setAiTurnCount] = useState(0); // Track AI turns for sound timing
  const aiTurnCountRef = useRef(0); // Use ref instead of state to avoid re-renders
  const [lastAIMove, setLastAIMove] = useState<{ column: number; row: number } | null>(null);

  /**
   * Updates the React state with current game engine state
   */
  const updateGameState = useCallback(() => {
    setGameState(engineRef.current.getGameState());
  }, []);

  /**
   * Handles AI move with delay for better UX
   */
  const handleAIMove = useCallback(async () => {
    if (!gameConfigRef.current || gameConfigRef.current.player2.type !== PlayerType.Computer) {
      setIsAIThinking(false);
      return;
    }

    // Double-check that it's actually AI's turn to prevent race conditions
    const currentState = engineRef.current.getGameState();
    if (currentState.currentPlayer !== 2 || currentState.status !== GameStatus.InProgress) {
      console.warn('AI move skipped, not AI turn or game over', currentState);
      setIsAIThinking(false);
      return;
    }

    aiTurnCountRef.current += 1;

    const shouldPlaySound = aiTurnCountRef.current % 2 === 0; // Change to % 3 for every third turn

    if (shouldPlaySound) {
      playRandomMyTurnSound();
    }

    // Wait for 3 seconds to allow sound to finish and provide better UX
    // This ensures the 1-second sound has time to play and creates anticipation
    await new Promise(resolve => setTimeout(resolve, 3000));

    const aiColumn = aiPlayerRef.current.makeMove(engineRef.current);
    if (aiColumn !== -1) {
      // Find target row BEFORE the move is applied so we can animate from the correct cell
      const boardSnapshot = engineRef.current.getGameState().board;
      let targetRow = -1;
      for (let r = boardSnapshot.length - 1; r >= 0; r--) {
        if (boardSnapshot[r][aiColumn] === null) {
          targetRow = r;
          break;
        }
      }

      const moveResult = engineRef.current.makeMove(aiColumn);
      if (moveResult.success) {
        playSound(SoundEffect.DropCoin);
        if (targetRow !== -1) {
          setLastAIMove({ column: aiColumn, row: targetRow });
        }
        updateGameState();
      }
    }

    setIsAIThinking(false);
  }, [updateGameState]);

  /**
   * Makes a move in the specified column
   */
  const makeMove = useCallback(
    (column: number): { success: boolean; error?: string } => {
      if (isAIThinking) {
        return { success: false, error: 'AI is thinking...' };
      }

      console.log(`Player is making move in column: ${column}`);

      const currentState = engineRef.current.getGameState();

      // Check if it's AI's turn
      if (
        gameConfigRef.current &&
        gameConfigRef.current.player2.type === PlayerType.Computer &&
        currentState.currentPlayer === 2
      ) {
        return { success: false, error: 'Wait for AI move' };
      }

      const result = engineRef.current.makeMove(column);
      if (result.success) {
        updateGameState();

        // Trigger AI move if needed
        const newState = engineRef.current.getGameState();
        if (
          gameConfigRef.current &&
          gameConfigRef.current.player2.type === PlayerType.Computer &&
          newState.currentPlayer === 2 &&
          newState.status === GameStatus.InProgress
        ) {
          // Set AI thinking immediately to prevent race conditions
          setIsAIThinking(true);

          // Clear any existing AI move timeout
          if (aiMoveTimeoutRef.current) {
            clearTimeout(aiMoveTimeoutRef.current);
          }

          // Use setTimeout to allow state update to complete
          aiMoveTimeoutRef.current = setTimeout(handleAIMove, 100);
        }
      }

      return result;
    },
    [isAIThinking, updateGameState, handleAIMove]
  );

  /**
   * Starts a new game with the specified configuration
   */
  const startNewGame = useCallback(
    (config: IGameConfig) => {
      gameConfigRef.current = config;
      engineRef.current.startNewGame();
      updateGameState();
      setAiTurnCount(0); // Reset AI turn counter for new game
      aiTurnCountRef.current = 0; // Reset AI turn counter for new game

      // If AI is the starting player, trigger its move; otherwise ensure thinking is cleared
      const initialState = engineRef.current.getGameState();
      if (
        config.player2.type === PlayerType.Computer &&
        initialState.currentPlayer === 2 &&
        initialState.status === GameStatus.InProgress
      ) {
        setIsAIThinking(true);
        if (aiMoveTimeoutRef.current) {
          clearTimeout(aiMoveTimeoutRef.current);
        }
        aiMoveTimeoutRef.current = setTimeout(handleAIMove, 100);
      } else {
        setIsAIThinking(false);
      }
    },
    [updateGameState, handleAIMove]
  );

  /**
   * Resets the current game
   */
  const resetGame = useCallback(() => {
    if (gameConfigRef.current) {
      startNewGame(gameConfigRef.current);
    } else {
      engineRef.current.startNewGame();
      updateGameState();
    }
  }, [startNewGame, updateGameState]);

  /**
   * Updates AI difficulty
   */
  const setAIDifficulty = useCallback((difficulty: AIDifficulty) => {
    console.log(`Setting AI difficulty to: ${difficulty}`);
    setAIDifficultyState(difficulty);
    aiPlayerRef.current.setDifficulty(difficulty);
    console.log(`AI difficulty updated. Current config:`, aiPlayerRef.current.getConfig());
  }, []);

  // Initialize AI difficulty
  useEffect(() => {
    aiPlayerRef.current.setDifficulty(aiDifficulty);
  }, [aiDifficulty]);

  // Cleanup AI timeout on unmount
  useEffect(() => {
    return () => {
      if (aiMoveTimeoutRef.current) {
        clearTimeout(aiMoveTimeoutRef.current);
      }
    };
  }, []);

  return {
    gameState,
    makeMove,
    startNewGame,
    resetGame,
    isAIThinking,
    aiDifficulty,
    setAIDifficulty,
    lastAIMove,
  };
};
